import React from 'react';
import { To, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Chip, 
  Stack, 
  useTheme,
  Fade
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface FilterToggleProps {
  backPath: To;
  showFilters: boolean;
  isFiltering: boolean;
  isLoading: boolean;
  toggleShowFilters: () => void;
  resultCount: number;
  onResetFilters?: () => void;
}

export const FilterToggle: React.FC<FilterToggleProps> = ({
  backPath,
  showFilters,
  isFiltering,
  isLoading,
  toggleShowFilters,
  resultCount = 0,
  onResetFilters
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Don't render when loading
  if (isLoading) return null;
  
  // Format result text
  let resultsText = `${resultCount} filtered results`;
  if (resultCount === 0) {
    resultsText = '0 filtered results';
  } else if (resultCount === 1) {
    resultsText = '1 filtered result';
  }
  
  return (
    <Stack 
      direction="row" 
      justifyContent="space-between" 
      alignItems="center" 
      spacing={2}
      sx={{ width: '100%' }}
    >
      <Box>
        {backPath && backPath !== '' && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
        )}
        
        {isFiltering && (
          <Fade in={isFiltering}>
            <Stack direction="row" spacing={1.5} sx={{
                  marginLeft: '20px',
            }}>
              <Chip
                icon={<WarningIcon fontSize="small" />}
                label="Filters are active"
                color="warning"
                variant="outlined"
                sx={{
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  pl: 0.5,
                  height: 36.5,
                  '& .MuiChip-icon': {
                    color: theme.palette.warning.main,
                    marginLeft: '4px',
                  },
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              />
              
              <Chip
                icon={resultCount > 0 ? <CheckCircleIcon fontSize="small" /> : <FilterAltIcon fontSize="small" />}
                label={resultsText}
                color={resultCount > 0 ? "success" : "error"}
                variant="outlined"
                sx={{
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  pl: 0.5,
                  height: 36.5,
                  '& .MuiChip-icon': {
                    color: resultCount > 0 ? theme.palette.success.main : theme.palette.error.main,
                    marginLeft: '4px',
                  },
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              />
              
              {onResetFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={onResetFilters}
                  sx={{
                    height: 36.5,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    borderColor: theme.palette.error.light,
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: `${theme.palette.error.light}10`,
                      borderColor: theme.palette.error.main
                    }
                  }}
                >
                  Reset filters
                </Button>
              )}
            </Stack>
          </Fade>
        )}
      </Box>
      
      <Button
        variant="contained"
        color="primary"
        startIcon={<FilterAltIcon />}
        disabled={isLoading}
        onClick={toggleShowFilters}
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontWeight: 500
        }}
      >
        {showFilters ? 'Hide filters' : 'Show filters'}
      </Button>
    </Stack>
  );
};

export default FilterToggle; 